/**
 * LiveDataMiniChart — CSS-only micro-visualization of the fresh rows
 * returned by `useLiveWidgets`. Rendered next to a widget row's live
 * badge so authors can eyeball whether the latest refresh looks
 * healthy before drilling into the full canvas.
 *
 * Renders bars for a `dimensions×1 measure` result, or a big-number
 * KPI when the widget type is `kpi`. No SVG / Recharts — this is a
 * 40×20 preview strip, not a full chart, so plain divs keep the
 * bundle size low and rendering cheap.
 */
import React from 'react';

/**
 * @param {{
 *   widget: {
 *     widget_id: string,
 *     widget_type?: string,
 *     chart_config?: {
 *       is_live?: boolean,
 *       structured_query?: {measures?: string[], dimensions?: string[]},
 *     },
 *   },
 *   liveData?: {
 *     rows?: Array<Record<string, any>>,
 *     columns?: string[],
 *     error?: string,
 *   },
 * }} props
 */
export function LiveDataMiniChart({ widget, liveData }) {
  if (!widget?.chart_config?.is_live) return null;
  if (!liveData || liveData.error) return null;
  const rows = liveData.rows || [];
  if (rows.length === 0) return null;
  const sq = widget.chart_config.structured_query || {};
  const measureKey = (sq.measures && sq.measures[0]) || null;
  const dimKey = (sq.dimensions && sq.dimensions[0]) || null;
  const isKpi = (widget.widget_type || '') === 'kpi';

  if (isKpi || !dimKey) {
    // Single-number KPI: sum the measure across returned rows so
    // filtered slices roll up cleanly.
    let total = 0;
    for (const r of rows) {
      const v = measureKey ? Number(r[measureKey]) : Number(r.value);
      if (Number.isFinite(v)) total += v;
    }
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-200"
        data-testid={`live-data-mini-kpi-${widget.widget_id}`}
      >
        <span className="text-[11px] font-semibold">
          {Number.isInteger(total) ? total.toLocaleString() : total.toFixed(2)}
        </span>
        {measureKey && <span className="opacity-60">{measureKey}</span>}
      </span>
    );
  }

  // Multi-row bar strip. Cap to 12 bars so a large refresh doesn't
  // stretch the row header.
  const capped = rows.slice(0, 12);
  const values = capped.map((r) => {
    const v = measureKey ? Number(r[measureKey]) : Number(r.value);
    return Number.isFinite(v) ? v : 0;
  });
  const maxV = Math.max(...values, 1);
  return (
    <div
      className="inline-flex items-end gap-[2px] h-4"
      data-testid={`live-data-mini-bars-${widget.widget_id}`}
      title={`${capped.length} live row${capped.length === 1 ? '' : 's'} · max ${maxV}`}
    >
      {capped.map((r, i) => {
        const v = values[i];
        const label = dimKey ? r[dimKey] : r.label;
        return (
          <div
            key={i}
            className="w-1 rounded-[1px] bg-emerald-400/80"
            style={{ height: `${Math.max(15, (v / maxV) * 100)}%` }}
            title={`${label}: ${v}`}
          />
        );
      })}
    </div>
  );
}

export default LiveDataMiniChart;
